import { Box, Grid, Stack, useTheme } from "@mui/material";
import { MprcButton, MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { IconCreatePublication, IconRepass } from "components/icons";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { FeedContentLoadStatus, PublicationApprovalStatus, PublicationType } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { UserSelectorDrawer } from "views/foundation/user/drawers/UserSelectorDrawer";
import { FavoriteButton, FollowButton, ShareButton } from "views/knowledge/components/card/behaviors/common/Interaction";
import { StickerBox } from "views/knowledge/components/card/behaviors/common/StickerBox";
import { useCardContext } from "views/knowledge/components/card/CardBuilder";
import { CardContextLink } from "views/knowledge/components/card/CardContextLink";
import { publicationFactory } from "views/knowledge/feed/FeedHelpers";
import { ModeratorMessageButton } from "../../actions/ModeratorMessageDialog";
import { DefaultModerationFooter } from "../common/DefaultModerationFooter";
import { QuestionOptions } from "../common/DefaultOptions";

///
export const DefaultTitle = () => {
    const { pub, pub: { questionDefinition, sticker, expired, availableInTheFuture } } = useCardContext();
    const theme = useTheme();
    const { t } = useTranslation("knowledge");

    const isBlocked = pub.blocked?.blockedOn != undefined;

    var borderStyle: React.CSSProperties | undefined = (pub.interaction.isNewPublication || pub.blocked?.firstAccessAfterBlock === true || pub.rejected?.firstAccessAfterRejected === true)
        ? { borderLeft: "4px solid", borderLeftColor: theme.palette.warning.main, paddingLeft: theme.spacing(1) }
        : undefined;

    var stickerDetail = questionDefinition!.hasDetail ? t("Feed.Card.Question.MoreDetailText") : "";
    stickerDetail += questionDefinition!.answerCount > 0
        ? `${questionDefinition!.hasDetail ? ' - ' : ''}${t("Feed.Card.Question.StickerAnswersCountText", { count: questionDefinition!.answerCount })}`
        : "";

    const color = isBlocked
        ? theme.palette.warning.main
        : expired || availableInTheFuture
            ? theme.palette.muted.light
            : theme.palette.common.black;

    const navigateUrl = KnowledgePaths.PublicationViewerResolver(pub.publicationId, pub.publicationType, pub.masterPublication?.publicationId);

    return (
        <div style={borderStyle}>
            {
                navigateUrl == document.location.pathname
                    ? (
                        <Box sx={{ color: color }}>
                            <MprcFeedTypography variant="title" component="div" noWrap={true} color={"inherit"}>
                                <span title={pub.title}>{pub.title}</span>
                            </MprcFeedTypography>
                        </Box>
                    )
                    : (
                        <CardContextLink
                            publicationId={pub.publicationId}
                            to={KnowledgePaths.PublicationViewerResolver(pub.publicationId, pub.publicationType)}
                        >
                            <Box sx={{ color: color }}>
                                <MprcFeedTypography variant="title" component="div" noWrap={true} color={"inherit"}>
                                    <span title={pub.title}>{pub.title}</span>
                                </MprcFeedTypography>
                            </Box>
                        </CardContextLink>
                    )
            }

            <Stack spacing={0.5} alignItems="flex-end" direction="row">
                {stickerDetail != "" && <StickerBox name={stickerDetail} textColor={theme.palette.common.white} backgroundColor={theme.palette.primary.light} />}
                {isBlocked && <StickerBox name={t("Feed.Card.Common.BlockedText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.warning.main} />}
                {sticker && <StickerBox name={sticker.name} textColor={sticker.textColor} backgroundColor={sticker.backgroundColor} />}
            </Stack>
        </div>
    );
};

///
export const DefaultContent = () => {
    const {
        pub: { publicationId, content },
        localContent,
        onUpdateLocalContent,
    } = useCardContext();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<any>(undefined);
    const api = useCoreApi();
    const { t } = useTranslation("knowledge");

    const loadContent = () => {
        setLoading(true);
        api.v1.knowledgePublication.getContent({ publicationId: publicationId })
            .then((result) => {
                onUpdateLocalContent({
                    content: result.data,
                    contentLoadStatus: FeedContentLoadStatus.AllContentLoaded,
                    state: result.data != null ? "expanded" : undefined
                });

            })
            .catch((e) => setError(e))
            .finally(() => setLoading(false));
    };

    const toogleState = () => onUpdateLocalContent({ ...localContent, state: localContent.state == "expanded" ? "contracted" : "expanded" });

    return error
        ? <MprcRequestErrorReport error={error} />
        : (
            <section id="k-content">
                {
                    localContent.contentLoadStatus == FeedContentLoadStatus.AllContentLoaded &&
                    <>
                        <MprcFeedTypography variant="content" component="div" className="ql-editor">
                            <div dangerouslySetInnerHTML={{ __html: localContent.state == "expanded" ? localContent.content! : content! }}></div>
                        </MprcFeedTypography>
                        <MprcFeedTypography variant="moreContent" component="a" onClick={toogleState}>
                            {localContent.state == "expanded" ? t("Feed.Card.Common.SeeLessText") : t("Feed.Card.Common.SeeMoreText")}
                        </MprcFeedTypography>
                    </>
                }

                {
                    localContent.contentLoadStatus == FeedContentLoadStatus.LoadContentInDemand &&
                    <>
                        <MprcFeedTypography variant="content" component="div" className="ql-editor">
                            <div dangerouslySetInnerHTML={{ __html: content ?? "" }}></div>
                        </MprcFeedTypography>
                        {
                            loading
                                ? <MprcSpinnerIndicator />
                                : <MprcFeedTypography variant="moreContent" component="a" onClick={() => loadContent()}>
                                    {t("Feed.Card.Common.SeeMoreText")}
                                </MprcFeedTypography>
                        }
                    </>
                }

                {
                    localContent.contentLoadStatus == FeedContentLoadStatus.NoActionNecessary &&
                    <>
                        <MprcFeedTypography variant="content" component="div" className="ql-editor">
                            <div dangerouslySetInnerHTML={{ __html: content ?? "" }}></div>
                        </MprcFeedTypography>
                    </>
                }
            </section>
        );
};

///
export const DefaultFooter = () => {

    const api = useCoreApi();
    const { pub: { publicationId, title, blocked, readOnly, approvalStatus }, onReload } = useCardContext();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const [dialog, setDialog] = useState<any>();

    ///
    const handleAnswer = () => {
        setDialog(publicationFactory(
            null,
            PublicationType.Answer,
            () => {
                setDialog(null);
                onReload();
            },
            { question: { questionId: publicationId, questionTitle: title } }
        ));
    };

    ///
    const handleRepass = () => {
        setDialog(<UserSelectorDrawer
            multi
            title={t("Feed.Card.Question.RepassDialogTitle")}
            subtitle={title}
            addSelectedButtonText={t("Feed.Card.Question.RepassAddSelectedButtonText")}
            onClose={
                (users) => {
                    if (users) {
                        api.v1.knowledgePublication.repassQuestion({
                            publicationId,
                            usersId: users.map(x => x.userId)
                        })
                            .then((result) => {
                                if (result) {
                                    enqueueSnackbar(t("Feed.Card.Question.RepassSuccessMessage"), { variant: "success" });
                                }
                            })
                            .catch((error) => { });
                    }
                    setDialog(null);
                }}
        />
        );
    };

    if (approvalStatus != PublicationApprovalStatus.Approved)
        return <DefaultModerationFooter />;

    return (
        <>
            {dialog}

            <Grid container justifyContent="space-between" alignItems="center">
                <Grid>
                    <Grid container spacing={1.5} justifyContent="center" alignItems="center">
                        {blocked?.blockedOn == undefined &&
                            <>
                                <Grid>
                                    <MprcButton
                                        disabled={readOnly}
                                        text={t("Feed.Card.Question.AnswerButtonText")}
                                        title={t("Feed.Card.Question.AnswerButtonTitle")}
                                        startIcon={<IconCreatePublication />}
                                        variant="contained"
                                        onClick={handleAnswer}
                                    />
                                </Grid>
                                <Grid>
                                    <MprcButton
                                        disabled={readOnly}
                                        text={t("Feed.Card.Question.RepassButtonText")}
                                        title={t("Feed.Card.Question.RepassButtonTitle")}
                                        color="muted"
                                        startIcon={<IconRepass />}
                                        onClick={handleRepass}
                                    />
                                </Grid>
                            </>
                        }
                        <Grid>
                            <FollowButton />
                        </Grid>
                    </Grid>
                </Grid>
                {blocked?.blockedOn != null &&
                    <Grid>
                        <ModeratorMessageButton optionMode="blocked" />
                    </Grid>
                }
                <Grid>
                    <Grid container justifyContent="center" alignItems="center">
                        <Grid>
                            <FavoriteButton />
                        </Grid>
                        <Grid>
                            <ShareButton />
                        </Grid>
                        <Grid>
                            <QuestionOptions />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};
